/**
 * Icon catalogue
 *
 * Builds a browseable, searchable, de-duplicated view over the raw `ICON_MAP`
 * registry in `config/icon-map.ts`. The registry contains many aliases that
 * point at the same underlying component (e.g. `alert` + `alert-circle`, or the
 * camelCase `chevronDown` mirror of `chevron-down`). For a picker we want each
 * glyph to appear exactly once, under a stable "canonical" name, while still
 * being findable by any of its aliases.
 *
 * Everything is derived from `ICON_MAP` at module load, so the catalogue stays
 * in sync automatically whenever an icon is added/removed from the registry.
 */

import { ICON_MAP, IconComponent, getIconNames, hasIcon } from '../../../config/icon-map';

export interface IconCatalogEntry {
  /** Canonical, DB-storable name (always a valid `ICON_MAP` key). */
  name: string;
  /** Every registry key that resolves to this glyph (used for search). */
  aliases: string[];
  /** Semantic category bucket this glyph was sorted into. */
  category: string;
  /** Pre-computed lowercase haystack: canonical + aliases + category. */
  searchText: string;
}

/**
 * Ordered category rules. First matching rule wins, so more specific buckets
 * come before broad ones. A rule matches if its pattern hits the canonical name
 * OR any alias. Anything unmatched falls through to `General`.
 */
const CATEGORY_RULES: { category: string; pattern: RegExp }[] = [
  { category: 'Brands', pattern: /^brand-/ },
  { category: 'Arrows', pattern: /(^|-)(arrow|arrows|chevron|chevrons|caret|corner)(-|$)/ },
  { category: 'Media', pattern: /(photo|image|camera|video|movie|music|volume|player|microphone|headphone|headset|disc|slideshow|presentation|cast|pause|^play$|^stop$|^record-)/ },
  { category: 'Files & Folders', pattern: /(file|folder|files|clipboard|paperclip|archive|copy|^note|notebook|^book|paste)/ },
  { category: 'Text & Editing', pattern: /(text|^align|bold|italic|underline|strikethrough|^h[1-6]$|heading|typography|markdown|quote|^list|indent|subscript|superscript|line-height|pencil|^edit|writing|signature|forms|^number$|brush|paint|palette|color)/ },
  { category: 'Communication', pattern: /(mail|message|chat|phone|bell|^send$|inbox|rss|speakerphone|notification|broadcast|^at$)/ },
  { category: 'Charts & Data', pattern: /(chart|graph|report|analytic|trending|gauge|timeline|^sum$|calculator|percentage|progress|database|table|columns)/ },
  { category: 'Devices & Tech', pattern: /(device|monitor|tablet|smartphone|server|printer|keyboard|mouse|terminal|^code|command|plug|wifi|router|qrcode|scan|satellite|cloud|power|battery|bug|^api|cpu)/ },
  { category: 'Commerce & Finance', pattern: /(cash|coin|currency|credit|wallet|receipt|shopping|gift|tag|ticket|^scale$|percentage)/ },
  { category: 'People', pattern: /(^user|users|mood|accessible|fingerprint|^id$)/ },
  { category: 'Maps & Places', pattern: /(^map|location|^pin$|pinned|globe|world|compass|plane|^car$|truck|^ship$|anchor|planet|rocket|building|^home$|school|church|leaf|umbrella|^sun$|^moon$)/ },
  { category: 'Layout & UI', pattern: /(layout|grid|sidebar|window|dashboard|component|template|frame|^menu|apps|category|stack|layers|^tabs$|maximize|minimize|resize|^focus$)/ },
  { category: 'Shapes', pattern: /(circle|square|triangle|hexagon|octagon|pentagon|polygon|rectangle|^oval$|diamond|^shape$|^point$|^star|heart|crown)/ },
  { category: 'Status & Security', pattern: /(alert|warning|^check|^ban$|^info|question|exclamation|circle-x|^x$|^lock|shield|^key$|^eye|help|flag|^slash$|^cross$)/ },
];

const FALLBACK_CATEGORY = 'General';

/**
 * Picks the canonical name for a group of aliases that share one component.
 * Prefer kebab/lowercase names, then names without digits, then the most
 * descriptive (longest), tie-broken alphabetically for determinism.
 */
function pickCanonicalName(names: string[]): string {
  const lower = names.filter((n) => n === n.toLowerCase());
  const pool = lower.length ? lower : names;
  const noDigit = pool.filter((n) => !/\d/.test(n));
  const finalPool = noDigit.length ? noDigit : pool;
  return [...finalPool].sort((a, b) => b.length - a.length || a.localeCompare(b))[0];
}

function categorize(names: string[]): string {
  for (const { category, pattern } of CATEGORY_RULES) {
    if (names.some((n) => pattern.test(n))) return category;
  }
  return FALLBACK_CATEGORY;
}

function buildCatalog(): IconCatalogEntry[] {
  // Group every registry key by the component it resolves to.
  const byComponent = new Map<IconComponent, string[]>();
  for (const name of getIconNames()) {
    const component = ICON_MAP[name];
    const group = byComponent.get(component);
    if (group) group.push(name);
    else byComponent.set(component, [name]);
  }

  const entries: IconCatalogEntry[] = [];
  byComponent.forEach((aliases) => {
    const name = pickCanonicalName(aliases);
    const category = categorize(aliases);
    entries.push({
      name,
      aliases,
      category,
      searchText: [...aliases, category].join(' ').toLowerCase(),
    });
  });

  return entries.sort((a, b) => a.name.localeCompare(b.name));
}

/** All distinct, de-duplicated glyphs, sorted by canonical name. */
export const ICON_CATALOG: IconCatalogEntry[] = buildCatalog();

/** `name -> entry`, keyed by EVERY alias so any valid key resolves. */
const ENTRY_BY_ALIAS = new Map<string, IconCatalogEntry>();
for (const entry of ICON_CATALOG) {
  for (const alias of entry.aliases) ENTRY_BY_ALIAS.set(alias, entry);
}

/**
 * Ordered list of categories that actually have icons, with `All` first.
 * Follows the CATEGORY_RULES order, then `General`.
 */
export const ICON_CATEGORIES: string[] = (() => {
  const present = new Set(ICON_CATALOG.map((e) => e.category));
  const ordered = CATEGORY_RULES.map((r) => r.category).filter((c) => present.has(c));
  if (present.has(FALLBACK_CATEGORY)) ordered.push(FALLBACK_CATEGORY);
  return ['All', ...ordered];
})();

/** True when `name` is a usable icon key (matches any alias in the registry). */
export function isValidIconName(name?: string | null): boolean {
  if (!name) return false;
  return hasIcon(name);
}

/** Resolves any valid alias to its canonical catalogue name; passes through otherwise. */
export function getCanonicalIconName(name?: string | null): string {
  if (!name) return '';
  return ENTRY_BY_ALIAS.get(name)?.name ?? name;
}

/** Looks up the full catalogue entry for any alias. */
export function getIconEntry(name?: string | null): IconCatalogEntry | undefined {
  if (!name) return undefined;
  return ENTRY_BY_ALIAS.get(name);
}

/**
 * Searches the catalogue. Optional category filter (`All`/undefined = no
 * filter). Results are ranked: exact name, then name prefix, then alias prefix,
 * then substring — so the most relevant glyphs surface first.
 */
export function searchIcons(query?: string, category?: string): IconCatalogEntry[] {
  const q = (query ?? '').trim().toLowerCase();
  const inCategory = (e: IconCatalogEntry) => !category || category === 'All' || e.category === category;

  if (!q) return ICON_CATALOG.filter(inCategory);

  const scored: { entry: IconCatalogEntry; score: number }[] = [];
  for (const entry of ICON_CATALOG) {
    if (!inCategory(entry)) continue;
    if (!entry.searchText.includes(q)) continue;

    let score = 4;
    if (entry.name === q) score = 0;
    else if (entry.name.startsWith(q)) score = 1;
    else if (entry.aliases.some((a) => a.startsWith(q))) score = 2;
    else if (entry.name.includes(q)) score = 3;
    scored.push({ entry, score });
  }

  return scored
    .sort((a, b) => a.score - b.score || a.entry.name.localeCompare(b.entry.name))
    .map((s) => s.entry);
}
